import type { Vec2 } from "@golazo/shared";

/**
 * Longitud de un vector, DETERMINISTA entre navegadores.
 *
 * Sustituye a `Math.hypot`, y no es una micro-optimizacion: es lo que decide
 * si dos jugadores ven el mismo partido.
 *
 * El estandar de JavaScript NO obliga a que `Math.hypot` (ni `sin`, `cos`,
 * `atan2`, `exp`, `log`) devuelva el mismo bit en todos los motores: dice que
 * son "implementation-approximated". Chrome, Safari y Firefox pueden diferir en
 * el ultimo bit, y en un juego con lockstep esa diferencia se multiplica tick a
 * tick hasta que dos pantallas cuentan partidos distintos.
 *
 * `Math.sqrt` SI esta clavado: IEEE-754 exige redondeo correcto, asi que
 * `sqrt(x*x + y*y)` da lo mismo en todas partes. Se pierde la proteccion de
 * `hypot` contra desbordamiento, y aqui da igual: las coordenadas del campo no
 * pasan de ~2000 y su cuadrado cabe de sobra en un double.
 */
export const len2 = (x: number, y: number): number => Math.sqrt(x * x + y * y);

export const vec = (x = 0, y = 0): Vec2 => ({ x, y });

export const clone = (v: Vec2): Vec2 => ({ x: v.x, y: v.y });

export const copyInto = (target: Vec2, source: Vec2): void => {
  target.x = source.x;
  target.y = source.y;
};

export const length = (v: Vec2): number => len2(v.x, v.y);

/** Normaliza en sitio. Un vector nulo se queda nulo. */
export const normalize = (v: Vec2): Vec2 => {
  const len = len2(v.x, v.y);
  if (len > 1e-6) {
    v.x /= len;
    v.y /= len;
  }
  return v;
};

/** Recorta la magnitud de `v` a `max`, en sitio. */
export const clampLength = (v: Vec2, max: number): Vec2 => {
  const len = len2(v.x, v.y);
  if (len > max && len > 1e-6) {
    const k = max / len;
    v.x *= k;
    v.y *= k;
  }
  return v;
};

export const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Distancia de un punto al SEGMENTO ab (no a la recta infinita).
 *
 * Hace falta para probar contactos contra el tramo que algo recorrio en un
 * tick, en vez de contra donde acabo: a 800 u/s el balon avanza 13 unidades por
 * tick y puede cruzar entera una zona de contacto entre dos muestreos.
 */
export const closestPointOnSegment = (p: Vec2, a: Vec2, b: Vec2): Vec2 => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq < 1e-9) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return { x: a.x + abx * t, y: a.y + aby * t };
};

/**
 * Distancia de un punto al SEGMENTO ab (no a la recta infinita).
 *
 * Hace falta para probar contactos contra el tramo que algo recorrio en un
 * tick, en vez de contra donde acabo: a 800 u/s el balon avanza 13 unidades por
 * tick y puede cruzar entera una zona de contacto entre dos muestreos.
 */
export const distToSegment = (p: Vec2, a: Vec2, b: Vec2): number => {
  const c = closestPointOnSegment(p, a, b);
  return len2(p.x - c.x, p.y - c.y);
};
